import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="provider-intelligence-bulk"
//
// Bulk selection for the Provider Intelligence table. Only individual provider
// rows (Location/EIN settings) carry a checkbox — system/group headers are not
// selectable. Shows an action bar with an Edit action that opens the bulk edit
// drawer, passing the selected setting IDs along.
export default class extends Controller {
  static targets = ["actionBar", "checkbox", "selectAll", "count", "editLink"]
  static values = { editUrl: String }

  connect() {
    this.refresh()
  }

  checkboxTargetConnected() {
    this.refresh()
  }

  checkboxTargetDisconnected() {
    this.refresh()
  }

  toggleAll(event) {
    this.checkboxes().forEach((checkbox) => {
      checkbox.checked = event.target.checked
    })
    this.refresh()
  }

  toggleOne() {
    this.refresh()
  }

  clearSelection(event) {
    event?.preventDefault()
    this.checkboxes().forEach((checkbox) => {
      checkbox.checked = false
    })
    this.refresh()
  }

  refresh() {
    const boxes = this.checkboxes()
    const checkedCount = boxes.filter((checkbox) => checkbox.checked).length

    if (this.hasCountTarget) {
      this.countTarget.textContent = checkedCount.toLocaleString()
    }

    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.toggle("hidden", checkedCount === 0)
      this.actionBarTarget.classList.toggle("flex", checkedCount > 0)
    }

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = boxes.length > 0 && checkedCount === boxes.length
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < boxes.length
    }
  }

  open(event) {
    event.preventDefault()
    const ids = this.selectedIds()
    if (ids.length === 0 || !this.hasEditUrlValue) return

    const url = new URL(this.editUrlValue, window.location.origin)
    // Carry the current table filter/view state (encoded in the page URL) into
    // the drawer so staging bulk changes re-renders the table with the same
    // filters still applied.
    const current = new URLSearchParams(window.location.search)
    current.forEach((value, key) => url.searchParams.append(key, value))
    ids.forEach((id) => url.searchParams.append("setting_ids[]", id))

    const drawerEvent = new Event("drawer.open")
    drawerEvent.url = url.toString()
    document.dispatchEvent(drawerEvent)
  }

  selectedIds() {
    return this.checkboxes()
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.piSettingId)
      .filter(Boolean)
  }

  checkboxes() {
    return Array.from(this.element.querySelectorAll("input[data-pi-setting-id]"))
  }
}
